/**
 * Calendly popup integration.
 *
 * Loads the official widget script lazily (only when the user actually
 * triggers a popup) so it doesn't slow down the initial page load.
 * The CSS for the widget is also lazily injected.
 *
 * Single source of truth for the Calendly URL is CALENDLY_URL below; if you
 * later change the meeting type, update only this constant.
 */

export const CALENDLY_URL = "https://calendly.com/richildinvest-info/30min";

const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CSS_HREF = "https://assets.calendly.com/assets/external/widget.css";

let scriptPromise = null;

function injectStylesheet() {
  if (typeof document === "undefined") return;
  if (document.querySelector(`link[href="${CSS_HREF}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = CSS_HREF;
  document.head.appendChild(link);
}

function loadScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.Calendly) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Calendly load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Calendly load failed"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Open the Calendly booking popup. Returns a promise that resolves when the
 * widget has been opened (or rejects if the script failed to load).
 *
 * Brand colors are passed so the embedded UI matches the Richild Invest palette
 * (navy primary + gold accent).
 */
export async function openCalendly(url = CALENDLY_URL) {
  if (typeof window === "undefined") return;
  injectStylesheet();
  try {
    await loadScript();
  } catch (err) {
    // Fallback: open Calendly in a new tab if the embed script can't load
    window.open(url, "_blank", "noopener");
    return;
  }
  if (!window.Calendly || typeof window.Calendly.initPopupWidget !== "function") {
    window.open(url, "_blank", "noopener");
    return;
  }
  window.Calendly.initPopupWidget({
    url,
    prefill: {},
    pageSettings: {
      backgroundColor: "ffffff",
      hideEventTypeDetails: false,
      hideLandingPageDetails: false,
      primaryColor: "0d2f4c",   // Richild navy
      textColor: "0d2f4c",
    },
    utm: {
      utmSource: "richildinvest.com",
      utmMedium: "hero-cta",
      utmCampaign: "book-a-call",
    },
  });

  // Push an analytics event so we can measure conversions in GA4
  if (typeof window.gtag === "function") {
    try {
      window.gtag("event", "book_a_call_opened", {
        event_category: "engagement",
        event_label: "Calendly popup opened",
      });
    } catch (_) { /* noop */ }
  }
}
